import React from 'react';
import Platform from "react-native-web/dist/exports/Platform";
import PropTypes from 'prop-types';
import ABBPropTypes from 'airbnb-prop-types';
import { useThemeTokens } from '../ThemeProvider';
import StackView from '../StackView';
import { a11yProps, getTokensPropType, focusHandlerProps, pressProps, selectSystemProps, useHash, useInputValue, useResponsiveProp, variantProp, viewProps, withLinkRouter } from '../utils';
import HorizontalScroll, { horizontalScrollUtils, HorizontalScrollButton } from '../HorizontalScroll';
import TabsItem from './TabsItem';
import TabsDropdown from './TabsDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const [selectItemProps, selectedItemPropTypes] = selectSystemProps([a11yProps, focusHandlerProps, pressProps, viewProps]);
const {
  selectHorizontalScrollTokens,
  useItemPositions
} = horizontalScrollUtils;
const getDefaultTabItemAccessibilityRole = parentRole => {
  switch (parentRole) {
    case 'tablist':
      return 'tab';
    case 'navigation':
      return 'link';
    default:
      return undefined;
  }
};
const getStackViewTokens = variant => {
  const equalWidth = variant?.equalWidth;
  return Platform.select({
    web: {
      justifyContent: equalWidth ? 'space-evenly' : 'flex-start',
      width: equalWidth ? '100%' : 'auto'
    },
    default: {}
  });
};

/**
 * Tabs renders a horizontally-scrolling menu of selectable buttons which may link
 * to a page or control what content is displayed on this page.
 *
 * By default, Tabs always renders as horizontal scrolling tabs regardless of viewport.
 * To enable dropdown mode, you must explicitly pass `variant={{ dropdown: true }}`.
 * When dropdown is enabled, it will only render as a dropdown on mobile and tablet
 * viewports (XS and SM). On larger viewports (MD, LG, XL), it will still render as
 * horizontal tabs even with dropdown enabled.
 *
 * If you are using Tabs to navigate to a new page (web-only) you should pass
 * `navigation`as the `accessibilityRole` to te Tabs component, this will cause
 * TabItems to default to a role of link and obtain aria-current behaviour.
 */
const Tabs = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    tokens,
    itemTokens,
    scrollButtonTokens,
    variant,
    value,
    initialValue,
    onChange,
    items = [],
    LinkRouter,
    linkRouterProps,
    ...rest
  } = _ref;
  const {
    space,
    ...themeTokens
  } = useThemeTokens('Tabs', tokens, variant);
  const {
    currentValue,
    setValue
  } = useInputValue({
    value,
    initialValue,
    onChange
  });
  const [itemPositions, isPositioningReady] = useItemPositions();
  useHash(React.useCallback((hash, event) => {
    const hashItem = hash && items.find(_ref2 => {
      let {
        href
      } = _ref2;
      return hash === href;
    });
    const hashId = hashItem && (hashItem.id || hashItem.label);
    if (hashId) setTimeout(setValue(hashId, event), 500);
  }, [items, setValue]), isPositioningReady);
  const restProps = selectProps(rest);
  const parentAccessibilityRole = restProps.accessibilityRole ?? 'tablist';
  const defaultTabItemAccessibiltyRole = getDefaultTabItemAccessibilityRole(parentAccessibilityRole);
  const stackViewTokens = getStackViewTokens(variant);

  // Render dropdown only if explicitly requested via variant AND viewport is xs or sm
  const isSmallViewport = useResponsiveProp({
    xs: true,
    sm: true,
    md: false,
    lg: false,
    xl: false
  }, false);
  const shouldRenderDropdown = variant?.dropdown === true && isSmallViewport;
  if (shouldRenderDropdown) {
    return /*#__PURE__*/_jsx(TabsDropdown, {
      ref: ref,
      tokens: tokens,
      itemTokens: itemTokens,
      variant: variant,
      value: currentValue,
      onChange: setValue,
      items: items,
      LinkRouter: LinkRouter,
      linkRouterProps: linkRouterProps,
      accessibilityRole: parentAccessibilityRole === 'tablist' ? 'button' : parentAccessibilityRole,
      ...restProps
    });
  }
  return /*#__PURE__*/_jsx(HorizontalScroll, {
    ref: ref,
    ScrollButton: HorizontalScrollButton,
    itemPositions: itemPositions,
    tokens: selectHorizontalScrollTokens(themeTokens),
    scrollButtonTokens: scrollButtonTokens,
    accessibilityRole: parentAccessibilityRole,
    ...restProps,
    children: /*#__PURE__*/_jsx(StackView, {
      space: space,
      direction: "row",
      tokens: stackViewTokens,
      children: items.map((_ref3, index) => {
        let {
          href,
          label,
          id,
          accessibilityRole = defaultTabItemAccessibiltyRole,
          onPress,
          ref: itemRef,
          LinkRouter: ItemLinkRouter = LinkRouter,
          linkRouterProps: itemLinkRouterProps,
          render,
          ...itemRest
        } = _ref3;
        const itemId = id ?? label;
        const isSelected = Boolean(currentValue && currentValue === itemId);
        const handlePress = event => {
          if (typeof onPress === 'function') onPress(event);
          setValue(itemId, event);
        };
        const itemProps = selectItemProps(itemRest);
        return /*#__PURE__*/_jsx(TabsItem, {
          ref: itemRef,
          id: itemId,
          href: href,
          variant: variant,
          tokens: itemTokens,
          onPress: handlePress,
          selected: isSelected,
          itemPositions: itemPositions,
          index: index,
          accessibilityRole: accessibilityRole,
          LinkRouter: ItemLinkRouter,
          linkRouterProps: {
            ...linkRouterProps,
            ...itemLinkRouterProps
          },
          render: render,
          ...itemProps,
          children: label
        }, itemId);
      })
    })
  });
});
Tabs.displayName = 'Tabs';
Tabs.propTypes = {
  ...selectedSystemPropTypes,
  ...withLinkRouter.propTypes,
  /**
   * Array of `TabsItem`s
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    ...selectedItemPropTypes,
    ...withLinkRouter.propTypes,
    href: PropTypes.string,
    label: PropTypes.string,
    id: PropTypes.string,
    ref: ABBPropTypes.ref(),
    render: PropTypes.func
  })),
  /**
   * `id` property of the current tab in the items array
   */
  value: PropTypes.string,
  initialValue: PropTypes.string,
  /**
   * Callback for when the selected tab changes
   */
  onChange: PropTypes.func,
  tokens: getTokensPropType('Tabs'),
  /**
   * Custom tokens for `TabsItem`
   */
  itemTokens: getTokensPropType('TabsItem'),
  /**
   * Custom tokens for `HorizontalScrollButton`
   */
  scrollButtonTokens: getTokensPropType('HorizontalScrollButton'),
  variant: variantProp.propType
};
export default Tabs;